class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Expose native open method for _openNativeCart() fallback only
    // This is the ONLY way the native drawer should open — never from open() directly
    this._nativeOpen = this._doNativeOpen.bind(this);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /**
   * open() — ALL cart open requests go through here.
   * 
   * Strategy: ALWAYS delegate to handleFloCartBtn() which handles:
   *   1. Try Shopflo immediately
   *   2. If SDK still loading, retry for 1.5s
   *   3. If Shopflo fails, call _openNativeCart() → _nativeOpen()
   * 
   * This method NEVER opens the native drawer directly.
   * This prevents "double drawer" (Shopflo + native both opening).
   */
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // Always delegate to the global handler — it has Shopflo-first + native fallback
    if (typeof window.handleFloCartBtn === 'function') {
      window.handleFloCartBtn();
      return;
    }

    // handleFloCartBtn not available (shouldn't happen, but safety net)
    // Only open native if Shopflo has definitively failed
    if (window._shopfloFailed) {
      this._doNativeOpen(triggeredBy);
    }
    // If Shopflo hasn't failed and handleFloCartBtn doesn't exist,
    // do nothing — don't risk a double drawer
  }

  /**
   * _doNativeOpen() — Opens the native Dawn cart drawer.
   * 
   * ONLY called by _openNativeCart() in theme.liquid AFTER Shopflo has definitively failed.
   * Never called directly from open() to prevent double-drawer.
   */
  _doNativeOpen(triggeredBy) {
    // Safety: refuse to open if Shopflo is confirmed active and working
    if (window.shopfloCartActive && !window._shopfloFailed) {
      console.log('[Cart] Native drawer blocked — Shopflo is active');
      return;
    }

    // Remove Shopflo blocking so native cart is visible
    document.body.classList.remove('shopflo-active');
    var blockStyle = document.getElementById('block-dawn-cart');
    if (blockStyle) blockStyle.remove();
    this.style.display = '';
    this.style.visibility = '';
    this.style.pointerEvents = '';

    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    }, 10);

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (typeof trapFocus === 'function') {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function' && this.activeElement) {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    if (typeof onKeyUpEscape === 'function') {
      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // Don't auto-open the cart — let the user click the cart icon
      // Just silently refresh Shopflo cart data if active
      if (window.shopfloCartActive && window.Shopflo && typeof window.Shopflo.refreshCart === 'function') {
        try { window.Shopflo.refreshCart(); } catch(e) {}
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);